import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '@/stores';
import { useSubscriberSessions, useLocations } from '@/hooks/useData';
import {
  Radio,
  Wifi,
  WifiOff,
  RefreshCw,
  Search,
  Filter,
  ArrowUpDown,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Copy,
  Check,
  User,
  Shield,
  Activity,
  HardDrive,
  Clock,
  MapPin,
  X
} from 'lucide-react';

interface SessionItem {
  subscription?: {
    id?: string;
    displayValue?: string;
    isExpired?: boolean;
  };
  zone?: {
    id?: string;
    displayValue?: string;
  };
  partner?: {
    id?: string;
    displayValue?: string;
  };
  status?: {
    id?: number; // 0 = Online, 1 = Offline
    entityType?: string;
    displayValue?: string;
  };
  customer?: {
    id?: string;
    displayValue?: string;
  };
  username?: string;
  ipAddress?: string | null;
  macAddress?: string | null;
  totalUploadedBytes?: number;
  totalDownloadedBytes?: number;
  createdAt?: string | null;
  sessionTimeInSeconds?: number | null;
}

const SubscriberSessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  // Common option style for select dropdowns to fix white-on-white text in dark mode
  const optionStyle: React.CSSProperties = {
    backgroundColor: isLight ? '#ffffff' : '#0f172a',
    color: isLight ? '#0f172a' : '#f8fafc',
    padding: '8px',
  };

  // Date formatting helper (YYYY-MM-DD)
  const getFormattedDateStr = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const initialTodayStr = getFormattedDateStr(new Date());

  // Pagination State
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Draft (Pending) Filter Form States in Sidebar
  const [draftShowOnline, setDraftShowOnline] = useState(true);
  const [draftShowOffline, setDraftShowOffline] = useState(true);
  const [draftDeviceName, setDraftDeviceName] = useState('');
  const [draftIncludeSubPartners, setDraftIncludeSubPartners] = useState(false);
  const [draftZone, setDraftZone] = useState<string>('all');
  const [draftDatePreset, setDraftDatePreset] = useState<'all' | 'today' | 'nextWeek' | 'custom'>('all');
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');

  // Applied Filter States (Passed to Query & Filter Processor)
  const [appliedShowOnline, setAppliedShowOnline] = useState(true);
  const [appliedShowOffline, setAppliedShowOffline] = useState(true);
  const [appliedDeviceName, setAppliedDeviceName] = useState('');
  const [appliedIncludeSubPartners, setAppliedIncludeSubPartners] = useState(false);
  const [appliedZone, setAppliedZone] = useState<string>('all');
  const [appliedDatePreset, setAppliedDatePreset] = useState<'all' | 'today' | 'nextWeek' | 'custom'>('all');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // Sort State
  const [sortProperty, setSortProperty] = useState<'username' | 'createdAt' | 'sessionTimeInSeconds'>('username');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // UI state
  const [unmaskedMacs, setUnmaskedMacs] = useState<Record<string, boolean>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(true);

  // Compute status query param based on APPLIED checkboxes
  const queryStatus = useMemo(() => {
    if (appliedShowOnline && !appliedShowOffline) return 0;
    if (!appliedShowOnline && appliedShowOffline) return 1;
    return undefined; // All
  }, [appliedShowOnline, appliedShowOffline]);

  // Query Hooks
  const { data: locationsData } = useLocations();
  const { data: rawData, isLoading, isFetching, error, refetch } = useSubscriberSessions({
    pageSize,
    pageNumber,
    status: queryStatus,
    username: appliedDeviceName,
    hierarchyLevel: appliedIncludeSubPartners ? 1 : 0,
    sortCriteriaProperty: sortProperty,
    sortCriteriaDirection: sortDirection,
    zone: appliedZone,
    datePreset: appliedDatePreset,
    startDate: appliedStartDate,
    endDate: appliedEndDate,
  });

  const isRefreshing = isLoading || isFetching;

  // Robustly extract items array
  const items: SessionItem[] = useMemo(() => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData;
    if (Array.isArray(rawData.items)) return rawData.items;
    if (Array.isArray(rawData.data)) return rawData.data;
    if (Array.isArray(rawData.model?.items)) return rawData.model.items;
    return [];
  }, [rawData]);

  // Format total count envelope
  const totalCount = useMemo(() => {
    if (!rawData) return 0;
    if (typeof rawData.totalCount === 'number') return rawData.totalCount;
    if (typeof rawData.model?.totalCount === 'number') return rawData.model.totalCount;
    return items.length;
  }, [rawData, items]);

  // Dynamically compute unique zone options
  const availableZones = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(locationsData)) {
      locationsData.forEach((loc: any) => {
        const name = loc.name || loc.zoneCode || loc.displayValue || loc.id;
        if (name) set.add(String(name));
      });
    }
    items.forEach(item => {
      const z = item.zone?.displayValue || item.zone?.id;
      if (z) set.add(String(z));
    });
    // Fallback default zones
    ['FNS0028', 'FNS0029', 'FNS0030', 'FNS0031'].forEach(z => set.add(z));
    return Array.from(set).sort();
  }, [locationsData, items]);

  // Filter items by APPLIED filters locally for instant response & portal alignment
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // 1. Status Filter
      const isOnline = item.status?.id === 0 || item.status?.displayValue?.toLowerCase() === 'online';
      if (!appliedShowOnline && !appliedShowOffline) {
        return false; // Neither checked -> show 0 items
      }
      if (appliedShowOnline && !appliedShowOffline && !isOnline) {
        return false;
      }
      if (!appliedShowOnline && appliedShowOffline && isOnline) {
        return false;
      }

      // 2. Zone Filter
      if (appliedZone !== 'all') {
        const itemZone = item.zone?.displayValue || item.zone?.id;
        if (itemZone !== appliedZone) return false;
      }

      // 3. Smart Device Name / Username / Subscriber Search Filter
      if (appliedDeviceName.trim() !== '') {
        const query = appliedDeviceName.trim().toLowerCase();
        const uName = (item.username || '').toLowerCase();
        const cName = (item.customer?.displayValue || '').toLowerCase();
        const subId = (item.subscription?.id || '').toLowerCase();
        const partnerName = (item.partner?.displayValue || '').toLowerCase();
        const ip = (item.ipAddress || '').toLowerCase();
        const mac = (item.macAddress || '').toLowerCase();
        if (!uName.includes(query) && !cName.includes(query) && !subId.includes(query) && !partnerName.includes(query) && !ip.includes(query) && !mac.includes(query)) {
          return false;
        }
      }

      // 4. Date Range Filter (Keep offline items with null createdAt)
      if (item.createdAt && (appliedDatePreset === 'custom' || appliedStartDate || appliedEndDate)) {
        const itemDate = new Date(item.createdAt);
        if (!isNaN(itemDate.getTime())) {
          if (appliedStartDate) {
            const start = new Date(appliedStartDate);
            start.setHours(0, 0, 0, 0);
            if (itemDate < start) return false;
          }
          if (appliedEndDate) {
            const end = new Date(appliedEndDate);
            end.setHours(23, 59, 59, 999);
            if (itemDate > end) return false;
          }
        }
      }

      return true;
    });
  }, [items, appliedShowOnline, appliedShowOffline, appliedZone, appliedDeviceName, appliedDatePreset, appliedStartDate, appliedEndDate]);

  // Date Preset Click Handler
  const handleDatePresetChange = (preset: 'all' | 'today' | 'nextWeek' | 'custom') => {
    setDraftDatePreset(preset);
    const now = new Date();
    if (preset === 'all') {
      setDraftStartDate('');
      setDraftEndDate('');
    } else if (preset === 'today') {
      const dStr = getFormattedDateStr(now);
      setDraftStartDate(dStr);
      setDraftEndDate(dStr);
    } else if (preset === 'nextWeek') {
      const startStr = getFormattedDateStr(now);
      const end = new Date(now.getTime() + 7 * 86400000);
      const endStr = getFormattedDateStr(end);
      setDraftStartDate(startStr);
      setDraftEndDate(endStr);
    }
  };

  // Handlers
  const handleApplyFilters = () => {
    setAppliedShowOnline(draftShowOnline);
    setAppliedShowOffline(draftShowOffline);
    setAppliedDeviceName(draftDeviceName);
    setAppliedIncludeSubPartners(draftIncludeSubPartners);
    setAppliedZone(draftZone);
    setAppliedDatePreset(draftDatePreset);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(draftEndDate);
    setPageNumber(1);
  };

  const handleResetFilters = () => {
    // Reset draft states
    setDraftShowOnline(true);
    setDraftShowOffline(true);
    setDraftDeviceName('');
    setDraftIncludeSubPartners(false);
    setDraftZone('all');
    setDraftDatePreset('all');
    setDraftStartDate('');
    setDraftEndDate('');

    // Reset applied states
    setAppliedShowOnline(true);
    setAppliedShowOffline(true);
    setAppliedDeviceName('');
    setAppliedIncludeSubPartners(false);
    setAppliedZone('all');
    setAppliedDatePreset('all');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setSortProperty('username');
    setSortDirection('asc');
    setPageNumber(1);
  };

  const toggleMacMask = (sessionId: string) => {
    setUnmaskedMacs(prev => ({ ...prev, [sessionId]: !prev[sessionId] }));
  };

  // Helper formatting functions
  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined || seconds < 0) return '—';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const pad = (n: number) => String(n).padStart(2, '0');
    if (hrs > 24) {
      const days = Math.floor(hrs / 24);
      const remHrs = hrs % 24;
      return `${days} يوم ${pad(remHrs)}:${pad(mins)}:${pad(secs)}`;
    }
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  };

  const formatDateArabic = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('ar-IQ', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    } catch (e) {
      return dateStr;
    }
  };

  const startRecord = (pageNumber - 1) * pageSize + 1;
  const endRecord = Math.min(pageNumber * pageSize, totalCount);

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      {/* Top Header & Sorting Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px'
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '22px', fontWeight: 700, margin: 0 }}>
              جلسات المشتركين
            </h1>
            <span
              style={{
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(99,102,241,0.1))',
                border: '1px solid rgba(59,130,246,0.3)',
                color: '#3b82f6',
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600
              }}
            >
              شبكة FTTH
            </span>
          </div>
          <p style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13px', marginTop: '4px' }}>
            مراقبة وعرض جلسات الاتصال النشطة لشبكة المشتركين من البوابة الوطنية
          </p>
        </div>

        {/* Counter & Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {totalCount > 0 && (
            <div
              style={{
                color: isLight ? '#475569' : '#94a3b8',
                fontSize: '13px',
                fontWeight: 600,
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.6)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                padding: '8px 14px'
              }}
            >
              عرض {startRecord} - {endRecord} من اصل {totalCount}
            </div>
          )}

          {/* Sort Selector */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <select
              value={`${sortProperty}-${sortDirection}`}
              onChange={e => {
                const [prop, dir] = e.target.value.split('-');
                setSortProperty(prop as any);
                setSortDirection(dir as any);
                setPageNumber(1);
              }}
              style={{
                appearance: 'none',
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.8)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                padding: '8px 36px 8px 12px',
                color: isLight ? '#0f172a' : '#e2e8f0',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="username-asc" style={optionStyle}>اسم الجهاز تصاعدي</option>
              <option value="username-desc" style={optionStyle}>اسم الجهاز تنازلي</option>
              <option value="createdAt-desc" style={optionStyle}>تاريخ الجلسة الأحدث</option>
              <option value="createdAt-asc" style={optionStyle}>تاريخ الجلسة الأقدم</option>
            </select>
            <ArrowUpDown size={14} style={{ position: 'absolute', right: '12px', pointerEvents: 'none', color: '#3b82f6' }} />
          </div>

          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: isLight ? '#2563eb' : '#60a5fa',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s'
            }}
          >
            <RefreshCw size={14} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
            تحديث
          </button>
        </div>
      </div>

      {/* Main Grid: Left/Center Content + Right Filter Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'start' }} className="subscriber-sessions-layout">
        
        {/* Session Cards Column */}
        <div>
          {isLoading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: isLight ? '#475569' : '#64748b', background: isLight ? '#fff' : 'rgba(13,31,60,0.4)', borderRadius: '16px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)' }}>
              <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6', marginBottom: '12px' }} />
              <div style={{ fontSize: '15px', fontWeight: 600 }}>جاري استجراف جلسات المشتركين من البوابة الوطنية...</div>
            </div>
          ) : error ? (
            <div
              style={{
                background: isLight ? '#fef2f2' : 'rgba(239,68,68,0.1)',
                border: isLight ? '1px solid #fca5a5' : '1px solid rgba(239,68,68,0.3)',
                borderRadius: '12px',
                padding: '20px',
                color: isLight ? '#b91c1c' : '#f87171',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              فشل تحميل جلسات المشتركين من البوابة. يرجى التاكد من الاتصال بالخادم.
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: isLight ? '#475569' : '#64748b', background: isLight ? '#fff' : 'rgba(13,31,60,0.4)', borderRadius: '16px', border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)' }}>
              <Radio size={36} style={{ color: '#64748b', marginBottom: '12px', opacity: 0.5 }} />
              <div style={{ fontSize: '15px', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>لا توجد جلسات متطابقة مع التصفية الحالية</div>
              <p style={{ fontSize: '13px', marginTop: '6px' }}>جرب تعديل خيارات التصفية أو النقر على زر "مسح الكل"</p>
              <button
                onClick={handleResetFilters}
                style={{ marginTop: '14px', background: '#e11d48', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                مسح الكل
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {filteredItems.map((sess, idx) => {
                const isOnline = sess.status?.id === 0 || sess.status?.displayValue?.toLowerCase() === 'online';
                const isExpired = sess.subscription?.isExpired ?? false;
                const subId = sess.subscription?.id || '—';
                const sessionIdKey = sess.subscription?.id || sess.username || String(idx);
                const isMacUnmasked = unmaskedMacs[sessionIdKey];

                const maskedMac = sess.macAddress
                  ? (isMacUnmasked ? sess.macAddress : '************')
                  : '—';

                return (
                  <div
                    key={sessionIdKey + idx}
                    style={{
                      background: isLight ? '#ffffff' : 'rgba(13,31,60,0.6)',
                      border: isOnline
                        ? (isLight ? '1px solid #bbf7d0' : '1px solid rgba(34,197,94,0.3)')
                        : (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)'),
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {/* Card Top Header */}
                    <div
                      style={{
                        padding: '12px 18px',
                        background: isOnline
                          ? (isLight ? 'linear-gradient(90deg, #f0fdf4, #dcfce7)' : 'linear-gradient(90deg, rgba(34,197,94,0.12), rgba(16,185,129,0.06))')
                          : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)'),
                        borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px',
                      }}
                    >
                      {/* Status Badges */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isOnline ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: '#15803d',
                              fontWeight: 700,
                              fontSize: '14px',
                            }}
                          >
                            <Activity size={18} style={{ color: '#22c55e' }} />
                            <span>متصل</span>
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: isLight ? '#64748b' : '#94a3b8',
                              fontWeight: 600,
                              fontSize: '14px',
                            }}
                          >
                            <WifiOff size={16} />
                            <span>غير متصل</span>
                          </div>
                        )}

                        <span
                          style={{
                            background: isExpired
                              ? (isLight ? '#fee2e2' : 'rgba(239,68,68,0.2)')
                              : (isLight ? '#dcfce7' : 'rgba(34,197,94,0.2)'),
                            color: isExpired ? '#ef4444' : '#22c55e',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          {isExpired ? 'غير نشط' : 'نشط'}
                        </span>
                      </div>

                      {/* Subscription Card ID */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', color: isLight ? '#64748b' : '#94a3b8' }}>
                          بطاقة تعريف
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: isLight ? '#0f172a' : '#38bdf8', fontFamily: 'monospace' }}>
                          {subId}
                        </span>
                      </div>
                    </div>

                    {/* Card Main Grid Info */}
                    <div style={{ padding: '16px 18px' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                          gap: '14px',
                          direction: 'rtl',
                        }}
                      >
                        {/* الاشتراك */}
                        <div>
                          <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#64748b', marginBottom: '3px' }}>الاشتراك</div>
                          <div style={{ fontSize: '13.5px', fontWeight: 700, color: isLight ? '#0f172a' : '#e2e8f0' }}>
                            {sess.subscription?.displayValue || '—'}
                          </div>
                        </div>

                        {/* اسم الجهاز */}
                        <div>
                          <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#64748b', marginBottom: '3px' }}>اسم الجهاز</div>
                          <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#3b82f6', fontFamily: 'monospace', direction: 'ltr', textAlign: 'right' }}>
                            {sess.username || '—'}
                          </div>
                        </div>

                        {/* المنطقة */}
                        <div>
                          <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#64748b', marginBottom: '3px' }}>المنطقة</div>
                          <div style={{ fontSize: '13.5px', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
                            {sess.zone?.displayValue || '—'}
                          </div>
                        </div>

                        {/* الوكيل */}
                        <div>
                          <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#64748b', marginBottom: '3px' }}>الوكيل</div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: isLight ? '#334155' : '#cbd5e1' }}>
                            {sess.partner?.displayValue || '—'}
                          </div>
                        </div>

                        {/* المشترك */}
                        <div>
                          <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#64748b', marginBottom: '3px' }}>المشترك</div>
                          <div style={{ fontSize: '13.5px', fontWeight: 700, color: isLight ? '#0f172a' : '#f1f5f9' }}>
                            {sess.customer?.displayValue || '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Technical Details Footer */}
                    <div
                      style={{
                        padding: '12px 18px',
                        background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.15)',
                        borderTop: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '14px',
                        fontSize: '12.5px',
                        color: isLight ? '#475569' : '#94a3b8',
                      }}
                    >
                      {/* MAC Address */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>MAC Address:</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: isLight ? '#0f172a' : '#cbd5e1', direction: 'ltr' }}>
                          {maskedMac}
                        </span>
                        {sess.macAddress && (
                          <button
                            onClick={() => toggleMacMask(sessionIdKey)}
                            title={isMacUnmasked ? 'إخفاء' : 'إظهار'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', padding: 0 }}
                          >
                            {isMacUnmasked ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        )}
                      </div>

                      {/* IP Address */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>IP Address:</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: isLight ? '#0f172a' : '#38bdf8', direction: 'ltr' }}>
                          {sess.ipAddress || '—'}
                        </span>
                      </div>

                      {/* بداية الجلسة */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={13} style={{ color: '#6366f1' }} />
                        <span>بداية الجلسة:</span>
                        <span style={{ fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
                          {formatDateArabic(sess.createdAt)}
                        </span>
                      </div>

                      {/* مدة الجلسة */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>مدة الجلسة:</span>
                        <span style={{ fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                          {formatDuration(sess.sessionTimeInSeconds)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {totalCount > pageSize && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '20px',
                padding: '14px 18px',
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.6)',
                borderRadius: '12px',
                border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              <div style={{ fontSize: '13px', color: isLight ? '#64748b' : '#94a3b8' }}>
                صفحة {pageNumber} من {Math.ceil(totalCount / pageSize)}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: pageNumber <= 1 ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : '#3b82f6',
                    color: pageNumber <= 1 ? (isLight ? '#94a3b8' : '#475569') : '#ffffff',
                    border: 'none',
                    cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  <ChevronRight size={16} /> السابق
                </button>

                <button
                  disabled={pageNumber * pageSize >= totalCount}
                  onClick={() => setPageNumber(p => p + 1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    background: pageNumber * pageSize >= totalCount ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') : '#3b82f6',
                    color: pageNumber * pageSize >= totalCount ? (isLight ? '#94a3b8' : '#475569') : '#ffffff',
                    border: 'none',
                    cursor: pageNumber * pageSize >= totalCount ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  التالي <ChevronLeft size={16} />
                </button>
              </div>

              {/* Page Size selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <span style={{ color: isLight ? '#64748b' : '#94a3b8' }}>عدد العناصر:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setPageNumber(1);
                  }}
                  style={{
                    background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.08)',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px'
                  }}
                >
                  <option value={10} style={optionStyle}>10</option>
                  <option value={20} style={optionStyle}>20</option>
                  <option value={50} style={optionStyle}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

      {/* CSS Media Query to place Filter Panel at the VERY TOP on Mobile Screens */}
      <style>{`
        @media (max-width: 900px) {
          .subscriber-sessions-layout {
            display: flex !important;
            flex-direction: column !important;
          }
          .subscriber-sessions-filter-panel {
            order: -1 !important;
            width: 100% !important;
            margin-bottom: 20px !important;
            box-sizing: border-box !important;
            padding: 16px 14px !important;
          }
          .desktop-filter-title {
            display: none !important;
          }
          .mobile-filter-header {
            display: flex !important;
          }
          .mobile-filter-body {
            display: ${isMobileFilterOpen ? 'block' : 'none'} !important;
          }
        }
        @media (min-width: 901px) {
          .desktop-filter-title {
            display: block !important;
          }
          .mobile-filter-header {
            display: none !important;
          }
          .mobile-filter-body {
            display: block !important;
          }
        }
      `}</style>

      {/* Right Sidebar Filter Panel (Top of list on Mobile, Sidebar on Desktop) */}
      <div
        className="subscriber-sessions-filter-panel"
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '22px 18px',
          boxShadow: isLight ? '0 4px 16px rgba(0,0,0,0.04)' : 'none',
        }}
      >
        {/* Desktop Header Title */}
        <div className="desktop-filter-title" style={{ textAlign: 'center', marginBottom: '20px', borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: isLight ? '#0f172a' : '#f1f5f9' }}>
            تصفية
          </h3>
        </div>

        {/* Mobile Filter Header (With Expand/Collapse Button) */}
        <div
          className="mobile-filter-header"
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: isMobileFilterOpen ? '14px' : '0px',
            borderBottom: isMobileFilterOpen ? (isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.06)') : 'none',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={18} style={{ color: '#e11d48' }} />
            <span style={{ fontSize: '16px', fontWeight: 800, color: isLight ? '#0f172a' : '#f1f5f9' }}>
              خيارات التصفية
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '12px',
                background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.15)',
                color: '#3b82f6'
              }}
            >
              {appliedShowOnline && appliedShowOffline ? 'الكل' : appliedShowOnline ? 'متصل' : 'غير متصل'}
            </span>
          </div>

          <button
            type="button"
            style={{
              background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '8px',
              padding: '6px',
              color: isLight ? '#0f172a' : '#f8fafc',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMobileFilterOpen ? <ChevronRight size={18} style={{ transform: 'rotate(90deg)' }} /> : <ChevronLeft size={18} style={{ transform: 'rotate(-90deg)' }} />}
          </button>
        </div>

        {/* Collapsible / Expandable Filter Body */}
        <div className="mobile-filter-body" style={{ marginTop: '14px' }}>

          {/* Section 1: عرض (Status checkboxes) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13.5px', fontWeight: 600, color: isLight ? '#475569' : '#cbd5e1', display: 'block', marginBottom: '10px' }}>
              عرض
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px', color: isLight ? '#0f172a' : '#e2e8f0', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={draftShowOnline}
                  onChange={e => setDraftShowOnline(e.target.checked)}
                  style={{ accentColor: '#22c55e', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                متصل
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px', color: isLight ? '#0f172a' : '#e2e8f0', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={draftShowOffline}
                  onChange={e => setDraftShowOffline(e.target.checked)}
                  style={{ accentColor: '#ef4444', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                غير متصل
              </label>
            </div>
          </div>

          {/* Section 2: اسم الجهاز (Device Search Input - Image 2 style) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: isLight ? '#475569' : '#cbd5e1', display: 'block', marginBottom: '6px' }}>
              اسم الجهاز
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="اسم الجهاز..."
                value={draftDeviceName}
                onChange={e => setDraftDeviceName(e.target.value)}
                style={{
                  width: '100%',
                  background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.05)',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  color: isLight ? '#0f172a' : '#f8fafc',
                  fontSize: '13.5px',
                  outline: 'none'
                }}
              />
              {draftDeviceName && (
                <button
                  type="button"
                  onClick={() => setDraftDeviceName('')}
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Section 3: تضمين الشركاء الفرعيين (Include Sub-partners) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px', fontWeight: 600, color: isLight ? '#0f172a' : '#e2e8f0' }}>
              <input
                type="checkbox"
                checked={draftIncludeSubPartners}
                onChange={e => setDraftIncludeSubPartners(e.target.checked)}
                style={{ accentColor: '#3b82f6', width: '18px', height: '18px', cursor: 'pointer' }}
              />
              تضمين الشركاء الفرعيين
            </label>
          </div>

          {/* Section 4: المنطقة (Zone filter with explicit dark mode options fix) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: isLight ? '#475569' : '#cbd5e1', display: 'block', marginBottom: '6px' }}>
              المنطقة
            </label>
            <select
              value={draftZone}
              onChange={e => setDraftZone(e.target.value)}
              style={{
                width: '100%',
                background: isLight ? '#ffffff' : '#0f172a',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                padding: '10px 12px',
                color: isLight ? '#0f172a' : '#f8fafc',
                fontSize: '13.5px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all" style={optionStyle}>
                كافة المناطق
              </option>
              {availableZones.map(zone => (
                <option key={zone} value={zone} style={optionStyle}>
                  {zone}
                </option>
              ))}
            </select>
          </div>

          {/* Section 5: التاريخ (Date Filter Presets & Inputs) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: isLight ? '#475569' : '#cbd5e1', display: 'block', marginBottom: '8px' }}>
              التاريخ:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '12px' }}>
              {(['all', 'today', 'nextWeek', 'custom'] as const).map(preset => (
                <button
                  key={preset}
                  onClick={() => handleDatePresetChange(preset)}
                  style={{
                    padding: '8px 2px',
                    borderRadius: '8px',
                    border: draftDatePreset === preset ? '1px solid #3b82f6' : (isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)'),
                    background: draftDatePreset === preset ? (isLight ? '#eff6ff' : 'rgba(59,130,246,0.15)') : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'),
                    color: draftDatePreset === preset ? '#2563eb' : (isLight ? '#475569' : '#94a3b8'),
                    fontSize: '11px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {preset === 'all' && 'الجميع'}
                  {preset === 'today' && 'اليوم'}
                  {preset === 'nextWeek' && 'الاسبوع'}
                  {preset === 'custom' && 'تخصيص'}
                </button>
              ))}
            </div>

            {/* Date Range Inputs (Matching Image 2 DD/MM/YYYY inputs layout) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#94a3b8', display: 'block', marginBottom: '4px' }}>تاريخ البدء</span>
                <input
                  type="date"
                  placeholder="DD/MM/YYYY"
                  value={draftStartDate}
                  onChange={e => {
                    setDraftStartDate(e.target.value);
                    setDraftDatePreset('custom');
                  }}
                  style={{
                    width: '100%',
                    background: isLight ? '#ffffff' : '#0f172a',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    color: isLight ? '#0f172a' : '#f8fafc',
                    fontSize: '12px'
                  }}
                />
              </div>
              <div>
                <span style={{ fontSize: '11px', color: isLight ? '#64748b' : '#94a3b8', display: 'block', marginBottom: '4px' }}>تاريخ الانتهاء</span>
                <input
                  type="date"
                  placeholder="DD/MM/YYYY"
                  value={draftEndDate}
                  onChange={e => {
                    setDraftEndDate(e.target.value);
                    setDraftDatePreset('custom');
                  }}
                  style={{
                    width: '100%',
                    background: isLight ? '#ffffff' : '#0f172a',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '6px',
                    padding: '6px 8px',
                    color: isLight ? '#0f172a' : '#f8fafc',
                    fontSize: '12px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Section 6: Action Buttons at the Bottom (Matching Image 2 Red Filter Button & Clear All Text Button) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
            {/* Primary Red "تصفية" Button */}
            <button
              onClick={handleApplyFilters}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #e11d48 0%, #dc2626 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(225, 29, 72, 0.3)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Filter size={16} />
              تصفية
            </button>

            {/* Subtle "مسح الكل" Button below red button */}
            <button
              onClick={handleResetFilters}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                color: isLight ? '#64748b' : '#94a3b8',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '8px',
                transition: 'color 0.2s',
                textAlign: 'center'
              }}
            >
              مسح الكل
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

export default SubscriberSessionsPage;
